import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { ItemSold } from '#src/components/ItemPreview/ItemSold/index.tsx';
import { TokenDetailsRoot } from '#src/components/ItemPreview/ui.tsx';
import { AuctionDetails } from '#src/components/auction/Auction/AuctionDetails/index.tsx';
import type { Props as PrimaryBuyDetailsProps } from '#src/components/primaryBuy/PrimaryBuyDetails/index.tsx';
import { PrimaryBuyDetails } from '#src/components/primaryBuy/PrimaryBuyDetails/index.tsx';
import { useGetAuctionDetails } from '#src/hooks/offers/useGetAuctionDetails.ts';
import { useGetTokenSingleSaleDetails } from '#src/hooks/offers/useGetTokenSingleSaleDetails.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import { TokenOfferDetails } from './TokenOfferDetails/index.tsx';
import type { TokenDetails_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  card: TokenDetails_anyCard;
  stackedTokensCount?: number;
  isDesktopLayout: boolean;
  hideSorareUser?: boolean;
  hideOwner?: boolean;
  galleryOwnerSlug?: string;
  disableSportSpecific?: boolean;
  primaryBuyConfirmationOptions?: PrimaryBuyDetailsProps['primaryBuyConfirmationOptions'];
};

export const TokenDetails = ({
  card,
  stackedTokensCount,
  isDesktopLayout,
  hideSorareUser,
  hideOwner,
  galleryOwnerSlug,
  disableSportSpecific,
  primaryBuyConfirmationOptions,
}: Props) => {
  const auction = useGetAuctionDetails(card.latestEnglishAuction);
  const offer = useGetTokenSingleSaleDetails(card);
  const primaryBuy = useIsPrimaryBuyLive(card);

  if (primaryBuy && !auction && !offer) {
    return (
      <TokenDetailsRoot>
        <PrimaryBuyDetails
          card={card}
          isDesktopLayout={isDesktopLayout}
          hideSorareUser={hideSorareUser}
          primaryBuyConfirmationOptions={primaryBuyConfirmationOptions}
        />
      </TokenDetailsRoot>
    );
  }

  if (offer) {
    return (
      <TokenOfferDetails
        card={card}
        stackedTokensCount={stackedTokensCount}
        isDesktopLayout={isDesktopLayout}
      />
    );
  }

  if (auction && card.latestEnglishAuction) {
    return (
      <AuctionDetails
        auction={card.latestEnglishAuction}
        hideSorareUser={hideSorareUser}
        isDesktopLayout={isDesktopLayout}
        useConversionRate
      />
    );
  }

  return (
    <ItemSold
      card={card}
      hideOwner={hideOwner}
      galleryOwnerSlug={galleryOwnerSlug}
      disableSportSpecific={disableSportSpecific}
    />
  );
};

TokenDetails.fragments = {
  anyCard: gql`
    fragment TokenDetails_anyCard on AnyCardInterface {
      slug
      rarity: rarityTyped
      latestEnglishAuction {
        id
        ...useGetAuctionDetails_auction
        ...AuctionDetails_auction
      }
      livePrimaryOffer {
        id
      }
      ...useGetTokenSingleSaleDetails_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...ItemSold_anyCard
      ...PrimaryBuyDetails_anyCard
      ...TokenOfferDetails_anyCard
    }
    ${useGetAuctionDetails.fragments.auction}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${AuctionDetails.fragments.auction}
    ${ItemSold.fragments.anyCard}
    ${PrimaryBuyDetails.fragments.anyCard}
    ${TokenOfferDetails.fragments.anyCard}
  ` as TypedDocumentNode<TokenDetails_anyCard>,
};
